import { useEffect, useRef, useState, type ReactNode } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';

import { AppPrimaryButton } from '@components/ui/AppPrimaryButton/AppPrimaryButton';
import styles from './AppAnalyticsConsentBanner.module.css';

const BANNER_OFFSCREEN_POSITION = 'calc(100% + 2rem)';
const BANNER_FOCUS_CLEARANCE = 48;
const BANNER_BOTTOM_CLEARANCE = 20;
const BANNER_TRANSITION = {
  type: 'spring',
  stiffness: 360,
  damping: 38,
  mass: 1,
} as const;

export type AppAnalyticsConsentBannerProps = {
  open: boolean;
  ariaLabel: string;
  eyebrow: string;
  title: string;
  description: ReactNode;
  declineLabel: string;
  allowLabel: string;
  onDecline: () => void;
  onAllow: () => void;
};

export const AppAnalyticsConsentBanner = ({
  open,
  ariaLabel,
  eyebrow,
  title,
  description,
  declineLabel,
  allowLabel,
  onDecline,
  onAllow,
}: AppAnalyticsConsentBannerProps) => {
  const bannerRef = useRef<HTMLElement>(null);
  const shouldReduceMotion = useReducedMotion();
  const [reservedHeight, setReservedHeight] = useState(0);
  const hiddenMotionState = {
    y: BANNER_OFFSCREEN_POSITION,
    opacity: 0.6,
  };

  useEffect(() => {
    if (!open || !bannerRef.current) {
      return;
    }

    const documentElement = document.documentElement;
    const originalScrollPaddingBottom = documentElement.style.scrollPaddingBottom;
    const banner = bannerRef.current;

    const reserveBannerSpace = () => {
      const nextReservedHeight = Math.ceil(
        banner.offsetHeight + BANNER_BOTTOM_CLEARANCE + BANNER_FOCUS_CLEARANCE
      );

      setReservedHeight(nextReservedHeight);
      documentElement.style.scrollPaddingBottom = `${nextReservedHeight}px`;
    };

    reserveBannerSpace();

    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(reserveBannerSpace);
    resizeObserver?.observe(banner);
    window.addEventListener('resize', reserveBannerSpace);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', reserveBannerSpace);
      documentElement.style.scrollPaddingBottom = originalScrollPaddingBottom;
      setReservedHeight(0);
    };
  }, [open]);

  return (
    <>
      <div
        aria-hidden="true"
        data-testid="analytics-consent-spacer"
        style={{ height: open ? reservedHeight : 0 }}
      />
      <AnimatePresence>
        {open ? (
          <motion.section
            ref={bannerRef}
            className={styles.banner}
            aria-label={ariaLabel}
            data-testid="analytics-consent-banner"
            initial={shouldReduceMotion ? false : hiddenMotionState}
            animate={{ y: 0, opacity: 1 }}
            exit={shouldReduceMotion ? { y: 0, opacity: 1 } : hiddenMotionState}
            transition={shouldReduceMotion ? { duration: 0 } : BANNER_TRANSITION}
          >
            <div className={styles.copy}>
              <p className={styles.eyebrow}>{eyebrow}</p>
              <h2 className={styles.title}>{title}</h2>
              <p className={styles.description}>{description}</p>
            </div>
            <div className={styles.actions}>
              <AppPrimaryButton type="default" onClick={onDecline} className={styles.actionButton}>
                {declineLabel}
              </AppPrimaryButton>
              <AppPrimaryButton type="primary" onClick={onAllow} className={styles.actionButton}>
                {allowLabel}
              </AppPrimaryButton>
            </div>
          </motion.section>
        ) : null}
      </AnimatePresence>
    </>
  );
};
